import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import SearchBar from '@/components/common/GlobalSearchModal/components/SearchBar';
import GameGrid from '@/components/common/GlobalSearchModal/components/GameGrid';
import Button from '@/components/common/Button/Button';
import { useGames } from '@/Context/GameContext';
import styles from './ProviderModal.module.css';

/**
 * ProviderModal Component
 * Modal for selecting game providers with search functionality
 * Similar to GlobalSearchModal but focused on provider selection
 */
const ProviderModal = ({ isOpen, onClose, onProvidersChange }) => {
  const navigate = useNavigate();
  const {
    selectedVendors,
    vendorDisplayNames,
    setSelectedVendors,
    searchGames,
    searchResults,
    totalCount,
    hasMorePages,
    isLoading,
    error,
    currentQuery,
    loadMoreGames,
    handleGameLaunch,
    clearError
  } = useGames();

  const { isMobile } = useScreenSize();
  const [providerSearchInput, setProviderSearchInput] = useState('');
  const [gameSearchInput, setGameSearchInput] = useState('');
  const [activeTab, setActiveTab] = useState('providers'); // 'providers' or 'games'
  const [viewMode, setViewMode] = useState('grid');
  const searchTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Memoized provider options
  const providerOptions = useMemo(() =>
    Object.entries(vendorDisplayNames).map(([key, name]) => ({ key, name })),
    [vendorDisplayNames]
  );

  // Filter providers based on search
  const filteredProviders = useMemo(() => {
    if (!providerSearchInput.trim()) {
      return providerOptions;
    }
    return providerOptions.filter(provider =>
      provider.name.toLowerCase().includes(providerSearchInput.toLowerCase()) ||
      provider.key.toLowerCase().includes(providerSearchInput.toLowerCase())
    );
  }, [providerOptions, providerSearchInput]);

  // Group providers by first letter for better organization
  const groupedProviders = useMemo(() => {
    const groups = {};
    filteredProviders.forEach(provider => {
      const firstLetter = provider.name.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(provider);
    });
    return groups;
  }, [filteredProviders]);

  // Initialize games search when switching to games tab
  useEffect(() => {
    if (activeTab === 'games' && isOpen) {
      // Clear any previous errors when initializing
      if (error) {
        clearError();
      }
      // Initialize search with current providers and search query
      // Only initialize once when switching to games tab
      // Skip updating currentQuery to avoid interfering with GlobalSearchModal
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        // Use empty query initially, or use gameSearchInput if user has typed
        const queryToUse = gameSearchInput || '';
        searchGames(queryToUse, selectedVendors, 1, {}, true); // true = skipUpdateQuery
      }
    }
  }, [activeTab, isOpen, selectedVendors, searchGames, error, clearError]);

  // Handle provider selection - Single selection only, closes modal immediately
  const handleProviderToggle = useCallback((providerKey) => {
    // Single selection: if clicking the same provider, deselect it; otherwise select only this one
    const newSelection = selectedVendors.includes(providerKey)
      ? [] // Deselect if already selected
      : [providerKey]; // Select only this provider (single selection)

    setSelectedVendors(newSelection);
    
    // Notify parent component of the change
    if (onProvidersChange) {
      onProvidersChange(newSelection);
    }
    
    // Close modal immediately after selection
    onClose();
  }, [selectedVendors, setSelectedVendors, onProvidersChange, onClose]);

  // Select all providers - Removed for single selection mode
  // Users can only select one provider at a time

  // Clear all providers
  const clearAllProviders = useCallback(() => {
    setSelectedVendors([]);
    if (onProvidersChange) {
      onProvidersChange([]);
    }
    // Close modal after clearing
    onClose();
  }, [setSelectedVendors, onProvidersChange, onClose]);

  // Handle provider search input
  const handleProviderSearchInput = useCallback((e) => {
    const value = e.target.value;
    setProviderSearchInput(value);
  }, []);

  // Handle game search input with debounce
  const handleGameSearchInput = useCallback((e) => {
    const value = e.target.value;
    setGameSearchInput(value);

    // Clear error when user starts typing
    if (error) {
      clearError();
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      // Skip updating currentQuery to avoid interfering with GlobalSearchModal
      searchGames(value, selectedVendors, 1, {}, true); // true = skipUpdateQuery
    }, 300);
  }, [selectedVendors, searchGames, error, clearError]);

  // Reset search and clear selection when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setProviderSearchInput('');
      setGameSearchInput('');
      hasInitializedRef.current = false;
      document.body.classList.remove('modal-open');
    } else {
      // When modal opens, clear all selections (no default selection)
      setSelectedVendors([]);
      setProviderSearchInput('');
      setGameSearchInput('');
      hasInitializedRef.current = false;
      document.body.classList.add('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, setSelectedVendors]);

  // Don't sync with currentQuery from GlobalSearchModal - keep ProviderModal search independent
  // This prevents duplication between the two modals

  // Clear error when search results are successfully loaded
  useEffect(() => {
    if (activeTab === 'games' && searchResults.length > 0 && error) {
      clearError();
    }
  }, [activeTab, searchResults.length, error, clearError]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div 
        className={`${styles.modalContainer} ${isMobile ? styles.mobileFullScreen : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          className={styles.closeButton} 
          onClick={onClose}
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Select Providers</h2>
        </div>

        <div className={styles.modalBody}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'providers' ? styles.tabActive : ''}`}
            onClick={() => {
              setActiveTab('providers');
              hasInitializedRef.current = false;
            }}
          >
            Providers
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'games' ? styles.tabActive : ''}`}
            onClick={() => {
              setActiveTab('games');
              // Clear error when switching tabs
              if (error) {
                clearError();
              }
              // Trigger search when switching to games tab
              // Skip updating currentQuery to avoid interfering with GlobalSearchModal
              if (!hasInitializedRef.current) {
                searchGames(gameSearchInput || '', selectedVendors, 1, {}, true); // true = skipUpdateQuery
                hasInitializedRef.current = true;
              }
            }}
          >
            Games
          </button>
        </div>

        {/* Search Bar */}
        <div className={styles.searchBarWrapper}>
          <SearchBar
            value={activeTab === 'providers' ? providerSearchInput : gameSearchInput}
            onChange={activeTab === 'providers' ? handleProviderSearchInput : handleGameSearchInput}
            placeholder={activeTab === 'providers' ? 'Search providers...' : 'Search games...'}
          />
        </div>

        {/* Provider List */}
        {activeTab === 'providers' && (
          <div className={styles.providerContent}>
            {/* Actions */}
            <div className={styles.actions}>
              {selectedVendors.length > 0 && (
                <button
                  className={styles.actionButton}
                  onClick={clearAllProviders}
                >
                  Clear
                </button>
              )}
              <span className={styles.selectedCount}>
                {selectedVendors.length > 0 
                  ? `Selected: ${vendorDisplayNames[selectedVendors[0]] || selectedVendors[0]}`
                  : 'Select a provider'}
              </span>
            </div>

            {/* Provider Grid */}
            <div className={styles.providerGrid}>
              {Object.entries(groupedProviders).map(([letter, providers]) => (
                <div key={letter} className={styles.providerGroup}>
                  <h3 className={styles.groupTitle}>{letter}</h3>
                  <div className={styles.providerList}>
                    {providers.map(provider => {
                      const isSelected = selectedVendors.includes(provider.key);
                      return (
                        <div
                          key={provider.key}
                          className={`${styles.providerItem} ${isSelected ? styles.providerItemSelected : ''}`}
                          onClick={() => handleProviderToggle(provider.key)}
                        >
                          <div className={styles.providerName}>{provider.name}</div>
                          {isSelected && (
                            <div className={styles.checkIcon}>
                              <Check size={16} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {filteredProviders.length === 0 && (
              <div className={styles.emptyState}>
                <p>No providers found matching "{providerSearchInput}"</p>
              </div>
            )}
          </div>
        )}

          {/* Games Tab */}
          {activeTab === 'games' && (
            <div className={styles.gamesContent}>
              {/* Stats Bar */}
              {searchResults.length > 0 && (
                <div className={styles.statsBar}>
                  <span>
                    Showing {searchResults.length} of {totalCount} games
                    {selectedVendors.length > 0 && ` from ${selectedVendors.length} provider(s)`}
                  </span>
                </div>
              )}

              {/* Error Banner - Only show when not loading and there's an error */}
              {error && !isLoading && (
                <div className={styles.errorBanner}>
                  <span>{error}</span>
                  <button className={styles.dismissButton} onClick={clearError}>
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Games Grid */}
              <div className={styles.gamesGridContainer}>
                <GameGrid
                  games={searchResults}
                  viewMode={viewMode}
                  onGameClick={(game) => handleGameLaunch(game, navigate)}
                  isLoading={isLoading}
                />

                {/* Load More Button */}
                {hasMorePages && searchResults.length > 0 && (
                  <div className={styles.loadMoreContainer}>
                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      onClick={loadMoreGames}
                      disabled={isLoading}
                      className={styles.loadMoreButton}
                    >
                      {isLoading ? 'Loading...' : 'Load More Games'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderModal;
